import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, X, Trash2, Copy } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Field } from "./Common";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function DayRow({ i, wins, open, onChange, testId }) {
  const def = open && !open.closed ? [open.open, open.close] : ["09:00", "17:00"];
  const setWin = (j, k, v) => onChange(wins.map((w, x) => (x === j ? (k === 0 ? [v, w[1]] : [w[0], v]) : w)));
  return (
    <div className="flex flex-wrap items-start gap-3 py-3 border-b last:border-0" data-testid={testId}>
      <label className="flex items-center gap-2 w-36 text-sm font-medium pt-2">
        <Switch data-testid={`${testId}-toggle`} checked={wins.length > 0} onCheckedChange={(v) => onChange(v ? [def] : [])} />{DAYS[i]}
      </label>
      <div className="flex-1 space-y-2">
        {!wins.length && <p className="text-sm text-muted-foreground pt-2">Not working</p>}
        {wins.map((w, j) => (
          <div key={j} className="flex items-center gap-2">
            <Input type="time" className="w-32" data-testid={`${testId}-start-${j}`} value={w[0]} onChange={(e) => setWin(j, 0, e.target.value)} />
            <span className="text-muted-foreground">–</span>
            <Input type="time" className="w-32" data-testid={`${testId}-end-${j}`} value={w[1]} onChange={(e) => setWin(j, 1, e.target.value)} />
            {wins.length > 1 && <Button size="icon" variant="ghost" data-testid={`${testId}-remove-${j}`} onClick={() => onChange(wins.filter((_, x) => x !== j))}><X className="h-4 w-4" /></Button>}
          </div>
        ))}
        {wins.length > 0 && wins.length < 3 && <button className="text-xs text-primary" data-testid={`${testId}-add`} onClick={() => onChange([...wins, [wins[wins.length - 1][1], def[1]]])}>+ Add another time window (e.g. after a break)</button>}
      </div>
      {open?.closed && wins.length > 0 && <p className="w-full text-xs text-amber-700">This location is closed on {DAYS[i]}s, so these hours won't be bookable.</p>}
    </div>
  );
}

export function WeeklyHours({ uid, data, locations, onSaved }) {
  const [sched, setSched] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSched(data.schedule || {}); }, [data]);
  const locs = locations.filter((l) => (data.location_ids || []).includes(l.id));
  const setDay = (lid, d, wins) => setSched({ ...sched, [lid]: { ...(sched[lid] || {}), [d]: wins } });
  const copyMon = (lid) => { const m = sched[lid]?.["0"] || []; setSched({ ...sched, [lid]: { ...(sched[lid] || {}), 1: m, 2: m, 3: m, 4: m } }); };
  const save = async () => {
    setBusy(true);
    try { await api.put(`/staff/${uid}/availability`, { schedule: sched }); toast.success("Working hours saved"); onSaved(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const empty = !Object.values(sched).some((days) => Object.values(days || {}).some((w) => w.length));
  if (!locs.length) return <p className="text-sm text-muted-foreground" data-testid="availability-no-locations">This person isn't assigned to any location yet. Assign locations in Staff first.</p>;
  return (
    <div className="space-y-4">
      {empty && <div className="rounded-[var(--radius)] border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-900" data-testid="availability-not-set">No weekly hours set yet, so {data.name} can currently be booked whenever the location is open.</div>}
      {locs.map((l) => (
        <section key={l.id} className="rounded-[var(--radius)] border bg-card p-5" data-testid={`availability-loc-${l.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
            <h2 className="text-lg font-semibold">{l.name}</h2>
            <Button size="sm" variant="ghost" data-testid={`availability-copy-${l.id}`} onClick={() => copyMon(l.id)}><Copy className="h-3.5 w-3.5 mr-1" />Copy Monday to Tue–Fri</Button>
          </div>
          {DAYS.map((_, i) => <DayRow key={i} i={i} wins={sched[l.id]?.[String(i)] || []} open={l.opening_hours?.[String(i)]} testId={`availability-${l.id}-day-${i}`} onChange={(w) => setDay(l.id, String(i), w)} />)}
        </section>
      ))}
      <Button className="rounded-full" data-testid="availability-save-btn" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save working hours"}</Button>
    </div>
  );
}

const OFF_BLANK = { start_date: "", end_date: "", all_day: true, start_time: "09:00", end_time: "13:00", reason: "" };

export function TimeOff({ uid, items, onChanged }) {
  const [f, setF] = useState(OFF_BLANK);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value, ...(k === "start_date" && (!f.end_date || f.end_date < e.target.value) ? { end_date: e.target.value } : {}) });
  const add = async () => {
    try {
      const { data } = await api.post(`/staff/${uid}/time-off`, f);
      toast.success("Time off added");
      if (data.conflicts.length) toast.warning(`${data.conflicts.length} existing booking(s) fall in this time off: ${data.conflicts.map((a) => `${a.date} ${a.start_time} ${a.client_name}`).join(", ")}. Please move them.`, { duration: 12000 });
      setF(OFF_BLANK); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = (id) => api.delete(`/staff/${uid}/time-off/${id}`).then(onChanged).catch((e) => toast.error(errMsg(e)));
  return (
    <section className="rounded-[var(--radius)] border bg-card p-5 space-y-4" data-testid="time-off-section">
      <div><h2 className="text-lg font-semibold">Time off & holidays</h2><p className="text-sm text-muted-foreground">Blocks online and staff bookings, including home visits.</p></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
        <Field label="From"><Input type="date" data-testid="time-off-start-date" value={f.start_date} onChange={set("start_date")} /></Field>
        <Field label="To"><Input type="date" data-testid="time-off-end-date" value={f.end_date} onChange={set("end_date")} /></Field>
        <Field label="Reason (optional)"><Input data-testid="time-off-reason" placeholder="Holiday, training…" value={f.reason} onChange={set("reason")} /></Field>
        <label className="flex items-center gap-2 text-sm h-10"><Switch data-testid="time-off-all-day" checked={f.all_day} onCheckedChange={(v) => setF({ ...f, all_day: v })} />All day</label>
        {!f.all_day && <><Field label="Start time"><Input type="time" data-testid="time-off-start-time" value={f.start_time} onChange={set("start_time")} /></Field>
          <Field label="End time"><Input type="time" data-testid="time-off-end-time" value={f.end_time} onChange={set("end_time")} /></Field></>}
      </div>
      <Button size="sm" className="rounded-full" data-testid="time-off-add-btn" disabled={!f.start_date || !f.end_date} onClick={add}><Plus className="h-4 w-4 mr-1" />Add time off</Button>
      <div className="divide-y" data-testid="time-off-list">
        {!items.length && <p className="text-sm text-muted-foreground py-2">No upcoming time off.</p>}
        {items.map((o) => (
          <div key={o.id} className="flex flex-wrap items-center gap-3 py-2 text-sm" data-testid={`time-off-${o.id}`}>
            <span className="font-mono">{o.start_date}{o.end_date !== o.start_date && ` → ${o.end_date}`}</span>
            <span className="text-muted-foreground">{o.all_day ? "All day" : `${o.start_time}–${o.end_time}`}</span>
            <span className="flex-1">{o.reason}</span>
            <Button size="icon" variant="ghost" data-testid={`time-off-delete-${o.id}`} onClick={() => del(o.id)}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
    </section>
  );
}
